/**
 * #742 owner-only control that records the current root AGENTS.md.
 * The chosen text is sent only inside `record_owner_contract` and is not kept here.
 */
import { useState, type ChangeEvent } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Guard } from '../App';
import { readOwnerContractFile, recordOwnerContract } from '../lib/record-owner-contract';

export function OwnerContractControl({ client, guard, orgId }: {
  client: SupabaseClient;
  guard: Guard;
  orgId: string;
}) {
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || busy) return;
    setBusy(true);
    setStatus(null);
    void (async () => {
      try {
        const read = await readOwnerContractFile(file);
        if (!read.ok) {
          setStatus(read.message);
          return;
        }
        const recorded = await recordOwnerContract({
          orgId,
          text: read.text,
          hash: read.hash,
          guard,
          rpc: (name, args) => client.rpc(name, args),
        });
        setStatus(recorded.message);
      } catch {
        setStatus('The owner contract was not recorded.');
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <section className="stack" aria-label="Record the owner contract">
      <strong>Owner contract</strong>
      <label className="row">
        <span>Record the current root AGENTS.md</span>
        <input
          aria-label="Record the current root AGENTS.md"
          type="file"
          disabled={busy}
          onChange={onChange}
        />
      </label>
      <small className="muted">This records the file you choose. The standing rules stay in that file, and this screen does not show the file text.</small>
      {status !== null && <p role="status">{status}</p>}
    </section>
  );
}
